"use client";

import { ArrowUpRightIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ComponentPropsWithoutRef, FC, Fragment, useMemo } from "react";
import { capitalizeFirstLetter } from "../utils/string";
import Typography from "./Typography";

export type BreadcrumbItem = {
  label: string;
  href: string;
  // Shown after the label and never cut off, e.g. a duration.
  detail?: string;
  // Opens in a new tab, marked by a ↗ that shows on hover, and always on
  // touch screens, which have no hover. The label truncates; the ↗ stays.
  external?: boolean;
};

type BreadcrumbProps = {
  labelsMapping?: { [key: string]: string };
  // An explicit trail, used instead of the one derived from the URL.
  items?: BreadcrumbItem[];
} & ComponentPropsWithoutRef<"nav">;

const Breadcrumb: FC<BreadcrumbProps> = ({
  labelsMapping = {},
  items,
  ...rest
}) => {
  const pathname = usePathname();

  // Memoize path segments computation to avoid recalculation on re-renders
  const pathSegments = useMemo(() => {
    // Remove hash fragment from path before processing
    const pathWithoutHash = (pathname || "").split("#")[0];

    return pathWithoutHash
      .split("/")
      .slice(1)
      .filter(
        (segment, index, array) =>
          !(segment === "page" || (index > 0 && array[index - 1] === "page"))
      )
      .map((segment) => segment.split("?")[0]);
  }, [pathname]);

  // Pre-compile RegExp patterns for dynamic segments
  const dynamicPatterns = useMemo(() => {
    return Object.keys(labelsMapping).reduce((patterns, key) => {
      // Check if this key contains regex special characters
      if (
        key.includes("*") ||
        key.includes("[") ||
        key.includes("(") ||
        key.includes("\\") ||
        key.includes("{") ||
        key.includes("}")
      ) {
        try {
          patterns[key] = new RegExp(`^${key}$`);
        } catch (e) {
          // Ignore invalid regex patterns
          console.warn(`Invalid regex pattern in breadcrumb: ${key}`);
        }
      }
      return patterns;
    }, {} as Record<string, RegExp>);
  }, [labelsMapping]);

  const getLabel = (segment: string): string => {
    // Direct match from mapping
    if (labelsMapping[segment]) {
      return labelsMapping[segment];
    }

    // Handle dynamic segments with pre-compiled patterns
    const dynamicKey = Object.keys(dynamicPatterns).find((key) => {
      try {
        return dynamicPatterns[key]?.test(segment);
      } catch (e) {
        return false;
      }
    });

    if (dynamicKey) {
      return labelsMapping[dynamicKey];
    }

    // Default formatting
    return capitalizeFirstLetter(segment.replace(/-/g, " "));
  };

  const crumbs: BreadcrumbItem[] =
    items ??
    pathSegments.map((segment, index) => ({
      label: getLabel(segment),
      href: "/" + pathSegments.slice(0, index + 1).join("/"),
    }));

  // Early return if no segments
  if (crumbs.length === 0) return null;

  return (
    <nav
      {...rest}
      className={`${rest.className || ""} flex items-center text-slate-500`}
    >
      {crumbs.map(({ label, href, detail, external }, index) => (
        <Fragment key={`${href}-${index}`}>
          {index > 0 && (
            <ChevronRightIcon className="w-4 h-4 text-slate-400 shrink-0 mx-1" />
          )}
          {external ? (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="group flex min-w-0 items-center gap-1"
            >
              <Typography variant="caption" className="truncate">
                {label}
              </Typography>
              {detail && (
                <Typography
                  variant="caption"
                  className="shrink-0 whitespace-nowrap"
                >
                  {detail}
                </Typography>
              )}
              <ArrowUpRightIcon
                aria-hidden
                className="size-3.5 shrink-0 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 [@media(hover:none)]:opacity-100"
              />
            </a>
          ) : (
            <Link href={href} className="text-ellipsis cursor-pointer">
              <Typography variant="caption">
                {detail ? `${label} ${detail}` : label}
              </Typography>
            </Link>
          )}
        </Fragment>
      ))}
    </nav>
  );
};

export default Breadcrumb;
